import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { API_BASE_URL } from '../../config/api';
import { authHeaders } from '../../services/auth';
import Spinner from '../../components/ui/Spinner';
import ToastProvider, { useToast } from '../../components/ui/ToastProvider';
import { adminTheme } from '../../theme-admin';
import { useSettings } from '../../contexts/SettingsContext';

// Page Layout
const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${adminTheme.spacing.xl};
  animation: slideIn 0.3s ease-out;
`;

const Title = styled.h2`
  font-family: ${adminTheme.fonts.heading};
  font-size: ${adminTheme.fontSizes['3xl']};
  font-weight: ${adminTheme.fontWeights.bold};
  color: ${adminTheme.colors.gray[900]};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.md};
`;

const SaveButton = styled.button`
  padding: ${adminTheme.spacing.md} ${adminTheme.spacing.xl};
  background: ${adminTheme.gradients.purple};
  color: white;
  border: none;
  border-radius: ${adminTheme.borderRadius.lg};
  font-weight: ${adminTheme.fontWeights.bold};
  font-size: ${adminTheme.fontSizes.base};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};
  box-shadow: ${adminTheme.shadows.md};
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.sm};

  &:hover {
    filter: brightness(1.1);
    transform: translateY(-2px);
    box-shadow: ${adminTheme.shadows.lg};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Tab Navigation
const TabNav = styled.div`
  display: flex;
  gap: ${adminTheme.spacing.xs};
  border-bottom: 2px solid ${adminTheme.colors.gray[200]};
  margin-bottom: ${adminTheme.spacing.lg};
`;

const Tab = styled.button`
  padding: ${adminTheme.spacing.md} ${adminTheme.spacing.lg};
  border: none;
  background: ${props => props.active ? adminTheme.colors.primary[50] : 'transparent'};
  color: ${props => props.active ? adminTheme.colors.primary[600] : adminTheme.colors.gray[600]};
  border-bottom: 3px solid ${props => props.active ? adminTheme.colors.primary[600] : 'transparent'};
  font-weight: ${props => props.active ? adminTheme.fontWeights.bold : adminTheme.fontWeights.medium};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};
  font-size: ${adminTheme.fontSizes.base};
  margin-bottom: -2px;

  &:hover {
    background: ${adminTheme.colors.primary[50]};
    color: ${adminTheme.colors.primary[600]};
  }
`;

// Form Components
const FormWrapper = styled.div`
  background: ${adminTheme.colors.background.primary};
  border: 1px solid ${adminTheme.colors.gray[200]};
  border-radius: ${adminTheme.borderRadius['2xl']};
  padding: ${adminTheme.spacing.xl};
  box-shadow: ${adminTheme.shadows.md};
`;

const FormContent = styled.div`
  display: grid;
  gap: ${adminTheme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${adminTheme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${adminTheme.fontSizes.sm};
  font-weight: ${adminTheme.fontWeights.semibold};
  color: ${adminTheme.colors.gray[700]};
`;

const Input = styled.input`
  padding: ${adminTheme.spacing.md};
  border: 1px solid ${adminTheme.colors.gray[300]};
  border-radius: ${adminTheme.borderRadius.lg};
  font-size: ${adminTheme.fontSizes.base};
  transition: ${adminTheme.transitions.fast};
  font-family: ${adminTheme.fonts.primary};

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${adminTheme.colors.primary[100]};
  }
`;

const Select = styled.select`
  padding: ${adminTheme.spacing.md};
  border: 1px solid ${adminTheme.colors.gray[300]};
  border-radius: ${adminTheme.borderRadius.lg};
  font-size: ${adminTheme.fontSizes.base};
  transition: ${adminTheme.transitions.fast};
  font-family: ${adminTheme.fonts.primary};
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${adminTheme.colors.primary[100]};
  }

  option {
    padding: ${adminTheme.spacing.sm};
  }
`;

const TextArea = styled.textarea`
  padding: ${adminTheme.spacing.md};
  border: 1px solid ${adminTheme.colors.gray[300]};
  border-radius: ${adminTheme.borderRadius.lg};
  font-size: ${adminTheme.fontSizes.base};
  transition: ${adminTheme.transitions.fast};
  font-family: ${adminTheme.fonts.primary};
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${adminTheme.colors.primary[100]};
  }
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${adminTheme.spacing.md};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MenuItemCard = styled.div`
  background: ${adminTheme.colors.gray[50]};
  border: 1px solid ${adminTheme.colors.gray[200]};
  border-radius: ${adminTheme.borderRadius.lg};
  padding: ${adminTheme.spacing.md};
  margin-bottom: ${adminTheme.spacing.md};
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${adminTheme.spacing.md};
  align-items: start;
`;

const MenuItemInputs = styled.div`
  display: grid;
  gap: ${adminTheme.spacing.sm};
`;

const MenuItemActions = styled.div`
  display: flex;
  gap: ${adminTheme.spacing.xs};
`;

const IconButton = styled.button`
  padding: ${adminTheme.spacing.sm};
  background: ${props => props.danger ? adminTheme.colors.danger[500] : adminTheme.colors.primary[500]};
  color: white;
  border: none;
  border-radius: ${adminTheme.borderRadius.md};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;

  &:hover {
    background: ${props => props.danger ? adminTheme.colors.danger[600] : adminTheme.colors.primary[600]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddButton = styled.button`
  padding: ${adminTheme.spacing.md} ${adminTheme.spacing.lg};
  background: ${adminTheme.gradients.green};
  color: white;
  border: none;
  border-radius: ${adminTheme.borderRadius.lg};
  font-weight: ${adminTheme.fontWeights.semibold};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.sm};
  margin-top: ${adminTheme.spacing.md};

  &:hover {
    filter: brightness(1.1);
    transform: translateY(-2px);
  }
`;

const InfoBox = styled.div`
  background: ${adminTheme.colors.primary[50]};
  border-left: 4px solid ${adminTheme.colors.primary[500]};
  padding: ${adminTheme.spacing.md};
  border-radius: ${adminTheme.borderRadius.md};
  margin-bottom: ${adminTheme.spacing.lg};
  
  p {
    margin: 0;
    color: ${adminTheme.colors.gray[700]};
    font-size: ${adminTheme.fontSizes.sm};
  }
`;

// Toggle Switch Components
const ToggleGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${adminTheme.spacing.lg};
  background: ${adminTheme.colors.gray[50]};
  border-radius: ${adminTheme.borderRadius.lg};
  border: 1px solid ${adminTheme.colors.gray[200]};
`;

const ToggleLabel = styled.div`
  flex: 1;
  
  .title {
    font-size: ${adminTheme.fontSizes.base};
    font-weight: ${adminTheme.fontWeights.semibold};
    color: ${adminTheme.colors.gray[900]};
    margin-bottom: ${adminTheme.spacing.xs};
  }
  
  .description {
    font-size: ${adminTheme.fontSizes.sm};
    color: ${adminTheme.colors.gray[600]};
  }
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 52px;
  height: 28px;
  flex-shrink: 0;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${adminTheme.colors.gray[300]};
    transition: ${adminTheme.transitions.default};
    border-radius: 28px;
  }
  
  .slider:before {
    position: absolute;
    content: "";
    height: 20px;
    width: 20px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: ${adminTheme.transitions.default};
    border-radius: 50%;
    box-shadow: ${adminTheme.shadows.sm};
  }
  
  input:checked + .slider {
    background: ${adminTheme.gradients.primary};
  }
  
  input:checked + .slider:before {
    transform: translateX(24px);
  }
  
  input:disabled + .slider {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

function PageBody() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [courses, setCourses] = useState([]);
  const toast = useToast();
  const { refreshSettings } = useSettings();

  const [config, setConfig] = useState({
    name: 'Service Hub IT Solutions',
    tagline: 'Best Software Training Institute',
    phone: '6301313334',
    phoneSecondary: '9876543210',
    email: 'infoservicehub4u@gmail.com',
    address: {
      street: '',
      city: '',
      state: '',
      country: 'India',
      zipCode: '',
      link: ''
    },
    additionalAddresses: [
      {
        label: 'Training Center',
        street: '2-11-148 vidyaranyapuri kic x road hanamkonda',
        city: 'Warangal',
        state: 'Telangana',
        country: 'India',
        zipCode: '506009',
        link: ''
      },
      {
        label: 'Branch Office',
        street: '',
        city: '',
        state: '',
        country: 'India',
        zipCode: '',
        link: ''
      }
    ],
    social: {
      facebook: '',
      twitter: '',
      linkedin: '',
      instagram: '',
      youtube: '',
      whatsapp: ''
    },
    navigation: {
      courseDropdownItems: []
    },
    features: {
      blogEnabled: true
    }
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/settings`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.data) {
          // Ensure features object exists with defaults
          const loadedData = {
            ...data.data,
            features: {
              blogEnabled: true,
              ...data.data.features
            }
          };
          setConfig(loadedData);
        }
      }
    } catch (error) {
      // No settings found, using defaults
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // Load courses for dropdown
    const loadCourses = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/courses`, { headers: authHeaders() });
        if (res.ok) {
          const data = await res.json();
          // Handle different response formats
          if (data.success && data.data) {
            if (Array.isArray(data.data.courses)) {
              setCourses(data.data.courses);
            } else if (Array.isArray(data.data)) {
              setCourses(data.data);
            } else {
              setCourses([]);
            }
          } else if (Array.isArray(data)) {
            setCourses(data);
          } else {
            setCourses([]);
          }
        }
      } catch (error) {
        setCourses([]); // Ensure courses is always an array
      }
    };
    loadCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify(config)
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to save settings');
      }

      // Refresh the global settings context so Header/Footer get updated immediately
      await refreshSettings();
      
      toast.success('Settings saved successfully! ✨');
    } catch (error) {
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader>
        <Title>
          <span>⚙️</span>
          Site Settings
        </Title>
        <SaveButton onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <span>⏳</span>
              Saving...
            </>
          ) : (
            <>
              <span>💾</span>
              Save Settings
            </>
          )}
        </SaveButton>
      </PageHeader>

      <InfoBox>
        <p>📝 Configure your website's global settings, contact information, and social media links here.</p>
      </InfoBox>

      <FormWrapper>
        <TabNav>
          <Tab active={activeTab === 'general'} onClick={() => setActiveTab('general')}>
            🏢 General Info
          </Tab>
          <Tab active={activeTab === 'contact'} onClick={() => setActiveTab('contact')}>
            📞 Contact Details
          </Tab>
          <Tab active={activeTab === 'social'} onClick={() => setActiveTab('social')}>
            🌐 Social Media
          </Tab>
          <Tab active={activeTab === 'navigation'} onClick={() => setActiveTab('navigation')}>
            🧭 Navigation
          </Tab>
          <Tab active={activeTab === 'features'} onClick={() => setActiveTab('features')}>
            ⚙️ Features
          </Tab>
        </TabNav>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Spinner label="Loading settings..." />
          </div>
        ) : (
          <FormContent>
            {/* GENERAL INFO TAB */}
            {activeTab === 'general' && (
              <>
                <FormGroup>
                  <Label>Tagline *</Label>
                  <Input
                    placeholder="Best Software Training Institute"
                    value={config.tagline}
                    onChange={(e) => setConfig({ ...config, tagline: e.target.value })}
                    required
                  />
                </FormGroup>
              </>
            )}

            {/* CONTACT DETAILS TAB */}
            {activeTab === 'contact' && (
              <>
                <h3 style={{ margin: '0 0 16px 0', color: '#1f2937', fontSize: '18px' }}>Phone Numbers</h3>
                <TwoColumn>
                  <FormGroup>
                    <Label>Primary Phone Number *</Label>
                    <Input
                      type="tel"
                      placeholder="6301313334"
                      value={config.phone}
                      onChange={(e) => setConfig({ ...config, phone: e.target.value })}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Secondary Phone Number</Label>
                    <Input
                      type="tel"
                      placeholder="9876543210"
                      value={config.phoneSecondary || ''}
                      onChange={(e) => setConfig({ ...config, phoneSecondary: e.target.value })}
                    />
                  </FormGroup>
                </TwoColumn>

                <h3 style={{ margin: '24px 0 16px 0', color: '#1f2937', fontSize: '18px' }}>Email</h3>
                <FormGroup>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    placeholder="infoservicehub4u@gmail.com"
                    value={config.email}
                    onChange={(e) => setConfig({ ...config, email: e.target.value })}
                    required
                  />
                </FormGroup>

                <h3 style={{ margin: '24px 0 16px 0', color: '#1f2937', fontSize: '18px' }}>Main Office Address (Primary)</h3>
                <FormGroup>
                  <Label>Street Address</Label>
                  <TextArea
                    placeholder="Enter street address"
                    value={config.address?.street || ''}
                    onChange={(e) => setConfig({ 
                      ...config, 
                      address: { ...config.address, street: e.target.value }
                    })}
                    rows={2}
                  />
                </FormGroup>

                <TwoColumn>
                  <FormGroup>
                    <Label>City</Label>
                    <Input
                      placeholder="City name"
                      value={config.address?.city || ''}
                      onChange={(e) => setConfig({ 
                        ...config, 
                        address: { ...config.address, city: e.target.value }
                      })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>State</Label>
                    <Input
                      placeholder="State name"
                      value={config.address?.state || ''}
                      onChange={(e) => setConfig({ 
                        ...config, 
                        address: { ...config.address, state: e.target.value }
                      })}
                    />
                  </FormGroup>
                </TwoColumn>

                <TwoColumn>
                  <FormGroup>
                    <Label>Country</Label>
                    <Input
                      placeholder="India"
                      value={config.address?.country || ''}
                      onChange={(e) => setConfig({ 
                        ...config, 
                        address: { ...config.address, country: e.target.value }
                      })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>ZIP Code</Label>
                    <Input
                      placeholder="123456"
                      value={config.address?.zipCode || ''}
                      onChange={(e) => setConfig({ 
                        ...config, 
                        address: { ...config.address, zipCode: e.target.value }
                      })}
                    />
                  </FormGroup>
                </TwoColumn>

                <FormGroup>
                  <Label>Google Maps Link (Get Directions)</Label>
                  <Input
                    type="url"
                    placeholder="https://maps.google.com/?q=..."
                    value={config.address?.link || ''}
                    onChange={(e) => setConfig({ 
                      ...config, 
                      address: { ...config.address, link: e.target.value }
                    })}
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Paste Google Maps link here. If empty, address will be auto-searched.
                  </small>
                </FormGroup>

                {/* Additional Addresses */}
                {config.additionalAddresses?.map((addr, index) => (
                  <div key={index} style={{ marginTop: '24px', padding: '20px', border: '2px solid #e5e7eb', borderRadius: '12px', background: '#f9fafb' }}>
                    <h3 style={{ margin: '0 0 16px 0', color: '#1f2937', fontSize: '18px' }}>
                      Additional Location {index + 1}: {addr.label || `Location ${index + 1}`}
                    </h3>
                    
                    <FormGroup>
                      <Label>Location Label</Label>
                      <Input
                        placeholder="e.g., Training Center, Branch Office"
                        value={addr.label || ''}
                        onChange={(e) => {
                          const newAddresses = [...config.additionalAddresses];
                          newAddresses[index] = { ...newAddresses[index], label: e.target.value };
                          setConfig({ ...config, additionalAddresses: newAddresses });
                        }}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Street Address</Label>
                      <TextArea
                        placeholder="Enter street address"
                        value={addr.street || ''}
                        onChange={(e) => {
                          const newAddresses = [...config.additionalAddresses];
                          newAddresses[index] = { ...newAddresses[index], street: e.target.value };
                          setConfig({ ...config, additionalAddresses: newAddresses });
                        }}
                        rows={2}
                      />
                    </FormGroup>

                    <TwoColumn>
                      <FormGroup>
                        <Label>City</Label>
                        <Input
                          placeholder="City name"
                          value={addr.city || ''}
                          onChange={(e) => {
                            const newAddresses = [...config.additionalAddresses];
                            newAddresses[index] = { ...newAddresses[index], city: e.target.value };
                            setConfig({ ...config, additionalAddresses: newAddresses });
                          }}
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label>State</Label>
                        <Input
                          placeholder="State name"
                          value={addr.state || ''}
                          onChange={(e) => {
                            const newAddresses = [...config.additionalAddresses];
                            newAddresses[index] = { ...newAddresses[index], state: e.target.value };
                            setConfig({ ...config, additionalAddresses: newAddresses });
                          }}
                        />
                      </FormGroup>
                    </TwoColumn>

                    <TwoColumn>
                      <FormGroup>
                        <Label>Country</Label>
                        <Input
                          placeholder="India"
                          value={addr.country || ''}
                          onChange={(e) => {
                            const newAddresses = [...config.additionalAddresses];
                            newAddresses[index] = { ...newAddresses[index], country: e.target.value };
                            setConfig({ ...config, additionalAddresses: newAddresses });
                          }}
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label>ZIP Code</Label>
                        <Input
                          placeholder="123456"
                          value={addr.zipCode || ''}
                          onChange={(e) => {
                            const newAddresses = [...config.additionalAddresses];
                            newAddresses[index] = { ...newAddresses[index], zipCode: e.target.value };
                            setConfig({ ...config, additionalAddresses: newAddresses });
                          }}
                        />
                      </FormGroup>
                    </TwoColumn>

                    <FormGroup>
                      <Label>Google Maps Link (Get Directions)</Label>
                      <Input
                        type="url"
                        placeholder="https://maps.google.com/?q=..."
                        value={addr.link || ''}
                        onChange={(e) => {
                          const newAddresses = [...config.additionalAddresses];
                          newAddresses[index] = { ...newAddresses[index], link: e.target.value };
                          setConfig({ ...config, additionalAddresses: newAddresses });
                        }}
                      />
                      <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Paste Google Maps link here. If empty, address will be auto-searched.
                      </small>
                    </FormGroup>

                    <button
                      type="button"
                      onClick={() => {
                        const newAddresses = config.additionalAddresses.filter((_, i) => i !== index);
                        setConfig({ ...config, additionalAddresses: newAddresses });
                      }}
                      style={{
                        marginTop: '12px',
                        padding: '10px 20px',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.background = '#dc2626'}
                      onMouseOut={(e) => e.target.style.background = '#ef4444'}
                    >
                      Remove This Location
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => {
                    const newAddress = {
                      label: '',
                      street: '',
                      city: '',
                      state: '',
                      country: 'India',
                      zipCode: '',
                      link: ''
                    };
                    setConfig({ 
                      ...config, 
                      additionalAddresses: [...(config.additionalAddresses || []), newAddress]
                    });
                  }}
                  style={{
                    marginTop: '16px',
                    padding: '12px 24px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#059669'}
                  onMouseOut={(e) => e.target.style.background = '#10b981'}
                >
                  + Add Additional Location
                </button>
              </>
            )}

            {/* SOCIAL MEDIA TAB */}
            {activeTab === 'social' && (
              <>
                <FormGroup>
                  <Label>WhatsApp Number (country code + number)</Label>
                  <Input
                    type="tel"
                    placeholder="e.g., 919876543210"
                    value={config.social?.whatsapp || ''}
                    onChange={(e) => setConfig({ 
                      ...config, 
                      social: { ...config.social, whatsapp: e.target.value }
                    })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Facebook URL</Label>
                  <Input
                    type="url"
                    placeholder="https://facebook.com/yourpage"
                    value={config.social?.facebook || ''}
                    onChange={(e) => setConfig({ 
                      ...config, 
                      social: { ...config.social, facebook: e.target.value }
                    })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Twitter URL</Label>
                  <Input
                    type="url"
                    placeholder="https://twitter.com/yourhandle"
                    value={config.social?.twitter || ''}
                    onChange={(e) => setConfig({ 
                      ...config, 
                      social: { ...config.social, twitter: e.target.value }
                    })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>LinkedIn URL</Label>
                  <Input
                    type="url"
                    placeholder="https://linkedin.com/company/yourcompany"
                    value={config.social?.linkedin || ''}
                    onChange={(e) => setConfig({ 
                      ...config, 
                      social: { ...config.social, linkedin: e.target.value }
                    })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Instagram URL</Label>
                  <Input
                    type="url"
                    placeholder="https://instagram.com/yourhandle"
                    value={config.social?.instagram || ''}
                    onChange={(e) => setConfig({ 
                      ...config, 
                      social: { ...config.social, instagram: e.target.value }
                    })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>YouTube URL</Label>
                  <Input
                    type="url"
                    placeholder="https://youtube.com/c/yourchannel"
                    value={config.social?.youtube || ''}
                    onChange={(e) => setConfig({ 
                      ...config, 
                      social: { ...config.social, youtube: e.target.value }
                    })}
                  />
                </FormGroup>
              </>
            )}

            {/* NAVIGATION TAB */}
            {activeTab === 'navigation' && (
              <>
                <InfoBox>
                  <p>🧭 Manage Course dropdown menu items. These will appear in the header navigation when users click "Courses".</p>
                  <p style={{ marginTop: '8px' }}>💡 <strong>Tip:</strong> Select a course from the dropdown and the name will auto-fill!</p>
                </InfoBox>

                <FormGroup>
                  <Label>Course Dropdown Menu Items</Label>
                  {config.navigation?.courseDropdownItems && config.navigation.courseDropdownItems.length > 0 ? (
                    config.navigation.courseDropdownItems.map((item, index) => (
                      <MenuItemCard key={index}>
                        <MenuItemInputs>
                          <Input
                            placeholder="Course Name (e.g., Web Development)"
                            value={item.name || ''}
                            onChange={(e) => {
                              const newItems = [...config.navigation.courseDropdownItems];
                              newItems[index] = { ...newItems[index], name: e.target.value };
                              setConfig({ 
                                ...config, 
                                navigation: { ...config.navigation, courseDropdownItems: newItems }
                              });
                            }}
                          />
                          <Select
                            value={item.path || ''}
                            onChange={(e) => {
                              const newItems = [...config.navigation.courseDropdownItems];
                              const selectedPath = e.target.value;
                              
                              // Auto-fill name if course is selected
                              if (selectedPath && selectedPath !== '/courses') {
                                const selectedCourse = courses.find(c => `/courses/${c.slug}` === selectedPath);
                                if (selectedCourse) {
                                  newItems[index] = { 
                                    ...newItems[index], 
                                    path: selectedPath,
                                    name: selectedCourse.title 
                                  };
                                } else {
                                  newItems[index] = { ...newItems[index], path: selectedPath };
                                }
                              } else if (selectedPath === '/courses') {
                                // If "All Courses" is selected
                                newItems[index] = { 
                                  ...newItems[index], 
                                  path: selectedPath,
                                  name: 'All Courses'
                                };
                              } else {
                                newItems[index] = { ...newItems[index], path: selectedPath };
                              }
                              
                              setConfig({ 
                                ...config, 
                                navigation: { ...config.navigation, courseDropdownItems: newItems }
                              });
                            }}
                          >
                            <option value="">Select a course...</option>
                            <option value="/courses">All Courses</option>
                            {Array.isArray(courses) && courses.map((course) => (
                              <option key={course._id} value={`/courses/${course.slug}`}>
                                {course.title}
                              </option>
                            ))}
                          </Select>
                        </MenuItemInputs>
                        <MenuItemActions>
                          <IconButton
                            danger
                            onClick={() => {
                              const newItems = config.navigation.courseDropdownItems.filter((_, i) => i !== index);
                              setConfig({ 
                                ...config, 
                                navigation: { ...config.navigation, courseDropdownItems: newItems }
                              });
                            }}
                          >
                            🗑️
                          </IconButton>
                        </MenuItemActions>
                      </MenuItemCard>
                    ))
                  ) : (
                    <InfoBox>
                      <p>No menu items yet. Click "Add Menu Item" to create your first course link.</p>
                    </InfoBox>
                  )}
                  
                  <AddButton
                    type="button"
                    onClick={() => {
                      const newItems = [
                        ...(config.navigation?.courseDropdownItems || []),
                        { name: '', path: '', order: (config.navigation?.courseDropdownItems?.length || 0) }
                      ];
                      setConfig({ 
                        ...config, 
                        navigation: { ...config.navigation, courseDropdownItems: newItems }
                      });
                    }}
                  >
                    <span>➕</span>
                    Add Menu Item
                  </AddButton>
                </FormGroup>
              </>
            )}

            {activeTab === 'features' && (
              <>
                <FormGroup>
                  <Label>Website Features</Label>
                  <InfoBox>
                    <p>Control which features are enabled on your website. Disabling a feature will hide it from the navigation and prevent access to those pages.</p>
                  </InfoBox>
                </FormGroup>

                <FormGroup>
                  <ToggleGroup>
                    <ToggleLabel>
                      <div className="title">📝 Blog & Articles</div>
                      <div className="description">
                        Enable or disable the blog section. When disabled, the Blog menu item and all blog pages will be hidden from visitors.
                      </div>
                    </ToggleLabel>
                    <ToggleSwitch>
                      <input
                        type="checkbox"
                        checked={config.features?.blogEnabled ?? true}
                        onChange={(e) => setConfig({
                          ...config,
                          features: {
                            ...config.features,
                            blogEnabled: e.target.checked
                          }
                        })}
                      />
                      <span className="slider"></span>
                    </ToggleSwitch>
                  </ToggleGroup>
                </FormGroup>
              </>
            )}
          </FormContent>
        )}
      </FormWrapper>
    </div>
  );
}

export default function AdminSettings() {
  return (
    <ToastProvider>
      <PageBody />
    </ToastProvider>
  );
}
